import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOSchema from '@/components/feature/SEOSchema';
import { categories, articles } from '@/mocks/data';

export default function Category() {
  const { slug = 'all' } = useParams();
  const [activeSlug, setActiveSlug] = useState(slug);

  const filteredArticles = useMemo(() => {
    if (activeSlug === 'all') return articles;
    return articles.filter((a) => a.category === activeSlug);
  }, [activeSlug]);

  const categoryName = categories.find((c) => c.slug === activeSlug)?.name || '全部';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `91牛 - ${categoryName}`,
    description: `浏览91牛平台的${categoryName}内容，了解更多关于91牛平台的信息与资源。`,
    url: `https://91niu.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '91牛',
      url: 'https://91niu.com/',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://91niu.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: `https://91niu.com/category/${activeSlug}`,
      },
    ],
  };

  return (
    <>
      <SEOSchema
        title={`91牛${categoryName} - 91牛平台资源中心`}
        description={`浏览91牛平台的${categoryName}内容，了解91牛的使用教程、功能介绍、最新活动及用户案例等丰富资源。`}
        keywords={`91牛${categoryName},91牛,91牛平台,91牛资源中心`}
        canonicalUrl={`https://91niu.com/category/${activeSlug}`}
        schema={[categorySchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            <div className="px-6 md:px-12 pb-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">
                资源中心
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => setActiveSlug(cat.slug)}
                    className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:text-primary-500 border border-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full">
                      {categories.find((c) => c.slug === article.category)?.name ||
                        article.category}
                    </div>
                  </div>
                  <div className="p-5">
                    <h2 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-foreground-500">该分类暂无内容</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}